import { Inbox } from "lucide-react";
import { Button } from "@onemileauto/ui";
import { useThreads } from "../../hooks/use-threads";
import { ThreadListItem } from "./thread-list-item";
import { EmptyState } from "./empty-state";

interface ThreadListProps {
  mailboxId: string | undefined;
  folderSlug: string;
  folderName?: string;
  selfEmail?: string;
  selectedThreadId?: string;
}

export function ThreadList({ mailboxId, folderSlug, folderName, selfEmail, selectedThreadId }: ThreadListProps) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useThreads(
    mailboxId,
    folderSlug,
  );

  if (isLoading) {
    return <div className="px-4 py-6 text-center text-sm text-muted">Loading&hellip;</div>;
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <p className="text-sm text-danger">Couldn&rsquo;t load this folder.</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const threads = data?.pages.flatMap((p) => p.threads) ?? [];

  if (threads.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title={`No mail in ${folderName?.toLowerCase() ?? "this folder"}`}
        description="New messages will show up here automatically — no need to refresh."
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div>
        {threads.map((thread) => (
          <ThreadListItem
            key={thread.id}
            thread={thread}
            mailboxId={mailboxId!}
            folderSlug={folderSlug}
            selfEmail={selfEmail}
            selected={thread.id === selectedThreadId}
          />
        ))}
      </div>
      {hasNextPage && (
        <div className="flex justify-center py-4">
          <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
